/** @jsxImportSource @emotion/react */

import { ApolloError, useApolloClient, useQuery } from '@apollo/client'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { DocumentNode } from 'graphql'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { FIND_MY_USER } from 'queries/common'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table, {
  BulkActionBase,
  BulkActions,
  FiltersWrapper,
  FooterWrapper,
  HeaderWrapper,
  PaginationPage,
  PaginationTake,
} from 'components/Table'
import { Search } from 'components/Input'
import Select from 'components/Select'
import Badge from 'components/Badge'
import Alert from 'components/Alert'
import { customMessages } from 'components/Alert/Alert'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import {
  findAllQuestion_findAllQuestion_items as Question,
  findAllQuestion_findAllQuestion_items_subjects as Subject,
} from '__generatedTypes__/findAllQuestion'
import {
  BULK_REMOVE_QUESTION,
  BULK_RESTORE_QUESTION,
  BULK_SOFT_REMOVE_QUESTION,
  CREATE_QUESTION,
} from 'queries/question'
import { createQuestion, createQuestionVariables } from '__generatedTypes__/createQuestion'
import { findMyUser } from '__generatedTypes__/findMyUser'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import Loader from 'components/Loader'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { toast } from 'utils/toast'
import { isImageUploadError } from 'utils/imageFile'
import { HIDDEN_SUBJECT_TITLE } from 'constants/consts'
import { searchQuestionCategory_searchQuestionCategory_items as QuestionCategory } from '__generatedTypes__/searchQuestionCategory'
import { AVAILABLE_HIDDEN_SUBJECTS } from '../Form/useQuestionHelpers'
import { getInitialValues, prepareForSubmit } from '../Form'
import { useInitialCategoryFromUrl } from '../useInitialCategoryFromUrl'
import { useDeletedQuestions, usePublishedQuestions, useQuestionsCount } from './useQuestions'
import { useQuestionFilters } from './useQuestionFilters'
import * as classes from './styles'
import { QUESTION_CATEGORY_PARAM, SHOW_DELETED_PARAM } from './consts'

export const getSubjectName = (subject?: Subject): string => {
  const hiddenSubject = AVAILABLE_HIDDEN_SUBJECTS.find((s) => s.id === subject?.id)

  if (hiddenSubject) {
    return hiddenSubject.tabName
  }

  if (subject?.tabName === HIDDEN_SUBJECT_TITLE) {
    return subject.platform.slug.toUpperCase()
  }

  return subject?.tabName ?? '-'
}

export type BulkAction = BulkActionBase & {
  getTitle: (count?: number) => string
  getSubtitle: (count?: number) => string | ReactElement
  mutationName: DocumentNode
}

const getActionSubtitle = (action: string, count: number) =>
  `Are you sure you want to ${action} ${count > 1 ? `these ${count} questions` : 'this question'}?`

const FAKE_BULK_ACTION_CLONE: BulkAction = {
  id: 'clone',
  title: 'Clone',
  getTitle: () => 'Clone',
  getSubtitle: (count?: number) => getActionSubtitle('clone', count ?? 1),
  mutationName: CREATE_QUESTION,
}

const BULK_ACTION_TRASH: BulkAction = {
  id: 'trash',
  title: 'Trash',
  getTitle: () => 'Move to Trash',
  getSubtitle: (count?: number) => getActionSubtitle('move to trash', count ?? 1),
  mutationName: BULK_SOFT_REMOVE_QUESTION,
}

const BULK_ACTION_RESTORE: BulkAction = {
  id: 'restore',
  title: 'Restore',
  getTitle: () => 'Restore',
  getSubtitle: (count?: number) => getActionSubtitle('restore', count ?? 1),
  mutationName: BULK_RESTORE_QUESTION,
}

const BULK_ACTION_DELETE: BulkAction = {
  id: 'delete',
  title: 'Delete',
  getTitle: () => 'Permanently Delete',
  getSubtitle: (count?: number) => getActionSubtitle('permanently delete', count ?? 1),
  mutationName: BULK_REMOVE_QUESTION,
}

const BULK_ACTIONS_PUBLISHED: BulkAction[] = [BULK_ACTION_TRASH]
const BULK_ACTIONS_DELETED: BulkAction[] = [BULK_ACTION_RESTORE, BULK_ACTION_DELETE]

export const List = () => {
  const { mutate } = useApolloClient()
  const { getLink } = useLink()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    categoryDisplayValue,
    setCategoryDisplayValue,
    loadingInitialCategory,
    initialCategoryFilter,
    errorInitialCategory,
  } = useInitialCategoryFromUrl()
  const { data: userData, loading: loadingUser } = useQuery<findMyUser>(FIND_MY_USER)
  const [isDeletedSelected, setIsDeletedSelected] = useState(() => searchParams.get(SHOW_DELETED_PARAM) === 'true')
  const [confirmItemAction, setConfirmItemAction] = useState<{ action: BulkAction; item: Question } | null>(null)
  const [confirmBulkAction, setConfirmBulkAction] = useState<BulkAction | null>(null)
  const [selectedItems, setSelectedItems] = useState<Question[]>([])
  const [isAlert, setIsAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const { questionCategory, questionBankOrPracticeTest } = useQuestionFilters()
  const questionsCount = useQuestionsCount()
  const publishedQuestionsState = usePublishedQuestions(initialCategoryFilter)
  const deletedQuestionsState = useDeletedQuestions(initialCategoryFilter)
  const selectedQuestionsState = isDeletedSelected ? deletedQuestionsState : publishedQuestionsState
  const { items, count, loading, refetch, pagination, search, order, filters, onFilterChange } = selectedQuestionsState

  useEffect(() => {
    if (isDeletedSelected) {
      publishedQuestionsState.refetch()
    } else {
      deletedQuestionsState.refetch()
    }
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    if (!loading) {
      setConfirmItemAction(null)
      setConfirmBulkAction(null)
      setSelectedItems([])
    }
  }, [loading])

  const handleChangeQuestionCategory = useCallback(
    (value: QuestionCategory | null) => {
      publishedQuestionsState.onFilterChange('questionCategory', value ?? undefined)
      deletedQuestionsState.onFilterChange('questionCategory', value ?? undefined)
      setCategoryDisplayValue(value)

      setSearchParams((prev) => {
        if (value) {
          prev.set(QUESTION_CATEGORY_PARAM, value.id)
        } else {
          prev.delete(QUESTION_CATEGORY_PARAM)
        }

        return prev.toString()
      })
    },
    [publishedQuestionsState, deletedQuestionsState, setSearchParams]
  )

  useEffect(() => {
    if (errorInitialCategory) {
      handleChangeQuestionCategory(null)
    }
  }, [errorInitialCategory])

  const handleServerError = useCallback((err: unknown) => {
    if (err instanceof Error) {
      if ('graphQLErrors' in err) {
        const gQLErr = err.graphQLErrors as ApolloError['graphQLErrors']
        if (gQLErr[0]?.extensions?.code == '405') setCustomMessage('usedQuestion')
      }
      setAlertMessage(err.message)
      setIsAlert(true)
    } else {
      toast.error('Failed to apply action')
    }
  }, [])

  const handleApplyBulkAction = useCallback(async () => {
    if (!selectedItems.length || !confirmBulkAction) {
      return
    }

    setConfirmBulkAction(null)
    setSelectedItems([])
    try {
      await mutate({
        mutation: confirmBulkAction.mutationName,
        variables: {
          ids: selectedItems.map(({ id }) => ({ id })),
        },
      })
      toast.success('Action applied successfully')
    } catch (err) {
      handleServerError(err)
    }
    refetch()
    questionsCount.refetch()
  }, [confirmBulkAction, selectedItems, refetch, questionsCount.refetch])

  const handleChangeTab = useCallback(
    (isDeleted: boolean) => {
      setIsDeletedSelected(isDeleted)
      setSearchParams((prev) => {
        if (isDeleted) {
          prev.set(SHOW_DELETED_PARAM, 'true')
        } else {
          prev.delete(SHOW_DELETED_PARAM)
        }
        return prev.toString()
      })
    },
    [setSearchParams]
  )

  const handleApplyItemAction = useCallback(async () => {
    if (!confirmItemAction) {
      return
    }

    setConfirmItemAction(null)

    if (confirmItemAction.action.id === 'clone') {
      try {
        const user = userData?.findMyUser ?? null
        const username = `${user?.firstName} ${user?.lastName}`
        const values = await getInitialValues(undefined, { ...confirmItemAction.item, history: [] })
        const questionData = await prepareForSubmit(values)

        const { data } = await mutate<createQuestion, createQuestionVariables>({
          mutation: confirmItemAction.action.mutationName,
          variables: {
            params: {
              ...questionData,
              title: `${questionData.title} - (copy)`,
              history: [
                {
                  updatedBy: username,
                  updatedAt: new Date().toISOString(),
                },
              ],
            },
          },
        })

        if (data?.createQuestion.id) {
          const link = getLink(cmsRoutes.QUESTION_EDIT, { questionId: data.createQuestion.id })
          window.open(link, '_blank')

          toast.success('Question cloned successfully')
        } else {
          toast.error('Failed to clone question')
        }
      } catch (e) {
        toast.error(isImageUploadError(e) ? e.message : 'Failed to clone question')
      }
    } else {
      try {
        await mutate({
          mutation: confirmItemAction.action.mutationName,
          variables: {
            ids: [{ id: confirmItemAction.item.id }],
          },
        })
        toast.success('Action applied successfully')
      } catch (err) {
        handleServerError(err)
      }
    }
    refetch()
    questionsCount.refetch()
  }, [confirmItemAction, refetch, userData, questionsCount.refetch])

  const getActionButton = useCallback(
    (action: BulkAction, item: Question) => (
      <Button
        variant="text"
        size="small"
        color="blue"
        css={classes.action}
        onClick={() => setConfirmItemAction({ action, item })}
        disabled={loading}
      >
        {action.title}
      </Button>
    ),
    [setConfirmItemAction, loading]
  )

  // A list that failed before its first load (e.g. API Gateway timeout) would otherwise keep the loader forever
  const notLoadedListStates = [publishedQuestionsState, deletedQuestionsState].filter((state) => !state.loaded)
  const listLoadError = notLoadedListStates.some((state) => state.loading)
    ? undefined
    : notLoadedListStates.find((state) => state.error)?.error

  const handleRetryLoad = () => {
    notLoadedListStates.forEach((state) => state.refetch())
    questionsCount.refetch()
  }

  if (listLoadError) {
    return (
      <Alert
        severity="error"
        message={`Failed to load questions: ${listLoadError.message}`}
        icon={false}
        action={
          <Button color="inherit" size="small" onClick={handleRetryLoad}>
            Retry
          </Button>
        }
      />
    )
  }

  if (!publishedQuestionsState.loaded || !deletedQuestionsState.loaded || loadingUser || loadingInitialCategory) {
    return (
      <div css={classes.headerWrapper}>
        <Loader />
      </div>
    )
  }

  return (
    <>
      <div css={classes.headerWrapper}>
        <div css={classes.linksWrapper}>
          <Link to={getLink(cmsRoutes.QUESTION_CATEGORY)}>
            <Button color="gray" variant="outlined">
              Edit <span className="hide-for-mobile">Question </span>Categories
            </Button>
          </Link>
          <Link to={getLink(cmsRoutes.QUESTION_CREATE)}>
            <Button color="blue" variant="contained">
              New Question
            </Button>
          </Link>
        </div>
      </div>
      {isAlert && (
        <Alert
          severity="error"
          customMessage={customMessage as keyof typeof customMessages}
          message={alertMessage}
          icon={false}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => {
                setIsAlert(false)
              }}
            >
              <CrossIcon fontSize="inherit" />
            </IconButton>
          }
        ></Alert>
      )}
      <div css={classes.toolbarWrapper}>
        <div css={classes.tabsWrapper}>
          <Button
            size="small"
            variant="text"
            onClick={() => handleChangeTab(false)}
            color={isDeletedSelected ? 'gray' : 'blue'}
            css={classes.action}
          >
            Published ({questionsCount.published})
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => handleChangeTab(true)}
            color={isDeletedSelected ? 'blue' : 'gray'}
            css={classes.action}
          >
            Deleted ({questionsCount.deleted})
          </Button>
        </div>
        <Search {...search} css={classes.fullWidthForMobile} />
      </div>
      <HeaderWrapper css={classes.filters}>
        <FiltersWrapper className="wrapper">
          <BulkActions
            bulkActions={isDeletedSelected ? BULK_ACTIONS_DELETED : BULK_ACTIONS_PUBLISHED}
            onApply={setConfirmBulkAction}
            disabled={!selectedItems.length || loading}
          />
          <Select
            items={questionCategory.items}
            value={categoryDisplayValue}
            onChange={handleChangeQuestionCategory}
            placeholder="All Question Categories"
            onInputChange={questionCategory.search}
            loading={questionCategory.loading}
            displaySelectedItemsFirst
            searchable
            css={classes.fullWidthForMobile}
          />
          {!isDeletedSelected ? (
            <Select
              items={questionBankOrPracticeTest.items}
              value={filters.questionBankOrPracticeTest ?? null}
              onChange={(value) => onFilterChange('questionBankOrPracticeTest', value ?? undefined)}
              placeholder="All Tests/Banks"
              onInputChange={questionBankOrPracticeTest.search}
              loading={questionBankOrPracticeTest.loading}
              displaySelectedItemsFirst
              searchable
              css={classes.fullWidthForMobile}
            />
          ) : (
            <></>
          )}
        </FiltersWrapper>
        <PaginationPage totalCount={count ?? 0} {...pagination} css={classes.pagination} />
      </HeaderWrapper>
      <Table
        css={classes.tableWrapper}
        columns={[
          {
            title: 'Title',
            renderCell: (question) => (
              <div css={classes.titleWrapper}>
                <div>{question.title}</div>
                {isDeletedSelected ? (
                  <div className="actions">
                    {getActionButton(BULK_ACTION_RESTORE, question)}
                    {getActionButton(BULK_ACTION_DELETE, question)}
                  </div>
                ) : (
                  <div className="actions">
                    <Link to={getLink(cmsRoutes.QUESTION_EDIT, { questionId: question.id })}>
                      <Button variant="text" size="small" color="blue" css={classes.action} disabled={loading}>
                        Edit
                      </Button>
                    </Link>
                    {getActionButton(BULK_ACTION_TRASH, question)}
                    {getActionButton(FAKE_BULK_ACTION_CLONE, question)}
                  </div>
                )}
              </div>
            ),
            flex: 2,
          },
          {
            title: 'Platform',
            renderCell: (question) =>
              question.subjects.length ? (
                <div css={classes.platformsWrapper}>
                  {question.subjects.map(({ platform }) => (
                    // TODO: how to resolve platform color?
                    <Badge
                      css={classes.badge}
                      key={platform.id}
                      text={getFullPlatformTitle(platform.title) ?? platform.title}
                      color="#2489FF"
                    />
                  ))}
                </div>
              ) : (
                '-'
              ),
            flex: 2,
          },
          {
            title: 'Subject',
            renderCell: (question) => getSubjectName(question.subjects[0]),
          },
          {
            title: 'Question Category',
            renderCell: (question) => question?.questionCategory?.title || '-',
          },
          {
            title: 'Date Created',
            renderCell: (question) => new Date(question.createdAt).toLocaleDateString(),
            flex: '0 1 124px',
            orderField: 'createdAt',
          },
        ]}
        items={items}
        selectedItems={selectedItems}
        order={order.value}
        onOrder={order.onChange}
        onSelect={setSelectedItems}
        isSelectable
        wrapperMinWidth={1100}
      />
      <FooterWrapper>
        <PaginationTake {...pagination} css={classes.paginationTake} />
      </FooterWrapper>
      <ConfirmationModal
        open={Boolean(confirmBulkAction && selectedItems.length)}
        title={confirmBulkAction?.getTitle(selectedItems.length) ?? ''}
        subtitle={confirmBulkAction?.getSubtitle(selectedItems.length) ?? ''}
        onClose={() => setConfirmBulkAction(null)}
        onConfirm={handleApplyBulkAction}
      />
      <ConfirmationModal
        open={Boolean(confirmItemAction)}
        title={confirmItemAction?.action.getTitle() ?? ''}
        subtitle={confirmItemAction?.action.getSubtitle() ?? ''}
        onClose={() => setConfirmItemAction(null)}
        onConfirm={handleApplyItemAction}
      />
    </>
  )
}
